import { useMemo } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import { assetDisplayLabel } from '../../../lib/formatUtils';

/**
 * Download + Email export buttons shown in the session summary.
 * Private to this file: each button renders only when its callback is supplied.
 */
function SessionExportActions({
  onExportPdf,
  onExportDefectReport,
  onEmailSessionReport,
  onEmailDefectReport,
  isSyncing,
}) {
  return (
    <div className="session-summary-export-actions">
      <div className="session-summary-action-group">
        <p className="session-summary-action-group-title">Download</p>
        <div className="session-summary-action-group-buttons">
          {onExportPdf && (
            <Button
              type="button"
              variant="secondary"
              onClick={onExportPdf}
              disabled={isSyncing}
              title="Download PDF Report"
            >
              Session PDF
            </Button>
          )}
          {onExportDefectReport && (
            <Button
              type="button"
              variant="danger"
              onClick={onExportDefectReport}
              disabled={isSyncing}
              title="Download Defect Report (failed tests only)"
            >
              Defect Report
            </Button>
          )}
        </div>
      </div>
      <div className="session-summary-action-group">
        <p className="session-summary-action-group-title">Email</p>
        <div className="session-summary-action-group-buttons">
          {onEmailSessionReport && (
            <Button
              type="button"
              variant="secondary"
              onClick={onEmailSessionReport}
              disabled={isSyncing}
              title="Email Session Report"
            >
              Session Email
            </Button>
          )}
          {onEmailDefectReport && (
            <Button
              type="button"
              variant="secondary"
              onClick={onEmailDefectReport}
              disabled={isSyncing}
              title="Email Defect Report"
            >
              Defect Email
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Finish-session summary modal: tested/passed/failed counts, failed-asset list,
 * and download/email export actions. Presentational — all data and handlers
 * come from the parent orchestrator via props.
 */
export function SessionSummaryModal({
  sessionResults,
  sessionAssets,
  onClose,
  onComplete,
  isFinishing,
  onExportPdf,
  onExportDefectReport,
  onEmailSessionReport,
  onEmailDefectReport,
  canExport,
  isSyncing,
}) {
  const entries = Object.entries(sessionResults || {});
  const totalTested = entries.length;
  const passed = entries.filter(([, r]) => r.status === 'pass').length;
  const failed = entries.filter(([, r]) => r.status === 'fail').length;
  const failedAssetIds = entries.filter(([, r]) => r.status === 'fail').map(([id]) => Number(id));
  const assetMap = useMemo(() => {
    const m = new Map();
    (sessionAssets || []).forEach((a) => m.set(a.id, a));
    return m;
  }, [sessionAssets]);

  return (
    <Modal isOpen onClose={onClose} title="Session Summary" className="session-summary-modal">
        <>
        <div className="session-summary-stats">
          <p><strong>Total Tested:</strong> {totalTested}</p>
          <p><strong>Passed:</strong> {passed}</p>
          <p><strong>Failed:</strong> {failed}</p>
        </div>
        {failedAssetIds.length > 0 && (
          <div className="session-summary-failed">
            <p><strong>Failed assets:</strong></p>
            <ul>
              {failedAssetIds.map((assetId) => {
                const asset = assetMap.get(assetId);
                const name = asset
                  ? assetDisplayLabel(asset)
                  : `Asset #${assetId}`;
                return <li key={assetId}>{name}</li>;
              })}
            </ul>
          </div>
        )}
        {canExport && (onExportPdf || onExportDefectReport) && (
          <SessionExportActions
            onExportPdf={onExportPdf}
            onExportDefectReport={onExportDefectReport}
            onEmailSessionReport={onEmailSessionReport}
            onEmailDefectReport={onEmailDefectReport}
            isSyncing={isSyncing}
          />
        )}
        <div className="modal-actions">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isFinishing}>
            Back to Session
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => onComplete()}
            disabled={isFinishing}
          >
            {isFinishing ? 'Finishing…' : 'Complete & Return to Dashboard'}
          </Button>
        </div>
        </>
    </Modal>
  );
}
